
import * as React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom-v5-compat';
import queryString from 'query-string';

import BySkill from './by_skill';
import ByStudent from './by_student';

import { SkillPerformanceInterface, } from '../interfaces'
import MobileSidebarButtonContainer from '../mobile_sidebar_button_container'
import { DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE, NO_CLASSROOM, diagnosticTypeOptions, timeframeOptions, } from '../shared'
import { requestGet, } from '../../../../../../modules/request'
import {
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  Spinner,
} from '../../../../../Shared/index';

const fileDocumentImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/file-document.svg`;

interface SkillPerformancesByDiagnosticTypeInterface {
  [key:number]: SkillPerformanceInterface[];
}

const PerformanceInsights = ({ openMobileSidebar, classroom, setSelectedDiagnostic, selectedDiagnostic, selectedSkill, setSelectedSkill, }) => {
  const location = useLocation();

  const timeframeFromLocalStorage = timeframeOptions.find(timeframe => timeframe.value === window.localStorage.getItem(DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE))

  const [selectedTimeframe, setSelectedTimeframe] = React.useState(timeframeFromLocalStorage || timeframeOptions[0]);
  const [skillPerformancesByDiagnosticType, setSkillPerformancesByDiagnosticType] = React.useState<SkillPerformancesByDiagnosticTypeInterface>({})

  const byStudent = location.pathname.includes('by_student');

  const classroomIdRef = React.useRef(classroom?.id);
  const skillPerformancesByDiagnosticTypeRef = React.useRef(skillPerformancesByDiagnosticType)

  React.useEffect(() => {
    classroomIdRef.current = classroom?.id;
  }, [classroom?.id]);

  React.useEffect(() => {
    skillPerformancesByDiagnosticTypeRef.current = skillPerformancesByDiagnosticType;
  }, [skillPerformancesByDiagnosticType]);

  React.useEffect(() => {
    if (!classroom) { return }

    if (classroom.id === NO_CLASSROOM.id) {
      setSkillPerformancesByDiagnosticType({})
    } else {
      setSkillPerformancesByDiagnosticType({})
      getSkillPerformancesByDiagnosticType()
    }
  }, [classroom, selectedTimeframe])

  React.useEffect(() => {
    if (!classroom) { return }

    if (skillPerformancesByDiagnosticType[selectedDiagnostic.value]) { return }

    getSkillPerformancesByDiagnosticType()
  }, [selectedDiagnostic])

  React.useEffect(() => {
    window.localStorage.setItem(DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE, selectedTimeframe.value.toString())
  }, [selectedTimeframe])

  React.useEffect(() => {
    if (!byStudent || !skillPerformancesByDiagnosticType[selectedDiagnostic.value]) { return }

    if (selectedSkill && skillOptions()?.find(skill => skill.value === selectedSkill.value)) { return }

    setSelectedSkill(skillOptions()[0])
  }, [byStudent, selectedDiagnostic, skillPerformancesByDiagnosticType])

  function skillOptions() {
    return skillPerformancesByDiagnosticType[selectedDiagnostic.value]
      ?.map(skill => ({ value: skill.skill_id, label: skill.skill_name, }))
      ?.sort((skillA, skillB) => skillA.label.localeCompare(skillB.label)) || []
  }

  function getSkillPerformancesByDiagnosticType() {
    const requestedClassroomId = classroom?.id;

    const queryHash = { timeframe: selectedTimeframe.value, };

    const url = queryString.stringifyUrl(
      {
        query: queryHash,
        url: `/diagnostic_insights/skill_performance_by_classroom/${requestedClassroomId}/${selectedDiagnostic.value}`,
      },
      { arrayFormat: 'bracket' }
    );

    requestGet(url, (body) => {
      // guards against overwriting the current classroom's data with a request that was fired earlier
      if (classroomIdRef.current === requestedClassroomId) {
        // using ref here guarantees other diagnostic levels don't get overwritten, regardless of when the requests fire and return
        setSkillPerformancesByDiagnosticType({...skillPerformancesByDiagnosticTypeRef.current, [selectedDiagnostic.value]: body});
      }
    });
  }

  const renderSkillDropdown = () => {
    const disabled = !skillOptions()?.length

    return skillOptions() && (
      <DropdownInput
        className={`small bordered ${disabled ? 'disabled' : ''}`}
        disabled={disabled}
        handleChange={setSelectedSkill}
        id="skill-dropdown"
        isSearchable={true}
        label='Skill'
        options={skillOptions()}
        value={skillOptions()?.find(skillOption => skillOption.value === selectedSkill?.value)}
      />
    )
  }

  if (!classroom) {
    return <Spinner />;
  }

  const diagnosticDropdown = (
    <DropdownInput
      className="small bordered"
      handleChange={setSelectedDiagnostic}
      id="diagnostic-type-dropdown"
      isSearchable={true}
      label='Diagnostic'
      options={diagnosticTypeOptions}
      value={selectedDiagnostic}
    />
  )

  const timeframeDropdown = (
    <DropdownInput
      className="small bordered"
      handleChange={setSelectedTimeframe}
      id="timeframe-dropdown"
      isSearchable={true}
      label='Timeframe'
      options={timeframeOptions}
      value={selectedTimeframe}
    />
  )

  const renderSubheader = () => {
    return (
      <div className="subheader">
        <div className="dropdown-controls">
          {diagnosticDropdown}
          {byStudent && renderSkillDropdown()}
          {timeframeDropdown}
        </div>
      </div>
    )
  }

  const routes = (
    <Routes>
      <Route
        element={
          <BySkill
            skillPerformances={skillPerformancesByDiagnosticType[selectedDiagnostic.value]}
          />
        }
        path="by_skill"
      />
      <Route
        element={
          <ByStudent
            selectedSkill={selectedSkill}
            skillPerformances={skillPerformancesByDiagnosticType[selectedDiagnostic.value]}
          />
        }
        path="by_student"
      />
    </Routes>
  )

  return (
    <main>
      <header>
        <h1>
          Performance Insights
          {byStudent ? <span>/ By Student</span> : <span>/ By Skill</span>}
        </h1>
        <a className="quill-button outlined small grey" href='https://support.quill.org/en/articles/10751323-how-do-teachers-use-diagnostic-insights' rel="noopener noreferrer" target="_blank">
          <img alt="" src={fileDocumentImgSrc} />
          Guide
        </a>
      </header>
      <MobileSidebarButtonContainer openMobileSidebar={openMobileSidebar} />
      {renderSubheader()}
      {routes}
    </main>
  );
};

export default PerformanceInsights;
